import * as React from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Box,
  Drawer,
  CssBaseline,
  Toolbar,
  IconButton,
  Badge,
  MenuItem,
  Menu,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import { AccountCircle } from "@mui/icons-material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MailIcon from "@mui/icons-material/Mail";
import MoreIcon from "@mui/icons-material/MoreVert";
import { MdChevronLeft, MdLogout, MdSettings } from "react-icons/md";
import logo from "../../../../assets/images/logo.svg";
import { PatientData } from "../Contexts";
import { useStyles } from "./Styles";
import AppDrawer from "./Drawer";
import PatientAlert from '../shared/PatientAlert';

const drawerWidth = 322;
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: `${drawerWidth}px`,
    }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "space-between",
}));

const propTypes = {
  notifications: PropTypes.array.isRequired,
  support_messages: PropTypes.array.isRequired,
  messages: PropTypes.array.isRequired,
  old: PropTypes.array.isRequired,
  completed_cprs: PropTypes.array.isRequired,
  cprs: PropTypes.array.isRequired,
  completed: PropTypes.object.isRequired,
  in_progress: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  window: PropTypes.func,
  children: PropTypes.object.isRequired,
};
AppMenu.prototype = propTypes;

export default function AppMenu(props) {
  const { window } = props;
  const patient = React.useContext(PatientData);
  const [open, setOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  let logoUrl = patient.logo_url || logo;
  let cprsWithOutcomeMessages = props.cprs.filter(function(cpr) { return (cpr['outcome_message'] != null); });
  const history = useHistory();
  const location = useLocation();
  const styles = useStyles();
  const [currentCprReviewerIndex, setCurrentCprReviewerIndex] = React.useState(0);

  const messageCount = props.messages
    ? props.messages.filter((i) => i.status !== "responded").length
    : null;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const renderAppDrawer = (
    <AppDrawer handleDrawerToggle={() => mobileOpen && setMobileOpen(false)} {...props} />
  );

  const menuId = "primary-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem>
        <a href="/patient_portal/settings" className="app-bar-link">
          <MdSettings />
          Settings
        </a>
      </MenuItem>
      <MenuItem>
        <a data-method="get" href="/users/sign_out" className="app-bar-link">
          <MdLogout />
          Logout
        </a>
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = "primary-account-menu-mobile";

  const renderNotificationsMenu = (
    <>
      {!!props.notifications.length > 0 ? (
        <IconButton
          className="nav-button"
          size="large"
          aria-label="show new CPR notifications"
          color="inherit"
          onClick={(e) => {
            history.push(`/patient_portal/notifications/${props.notifications[0].id}`);
          }}
        >
          <Badge badgeContent={props.notifications.length} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      ) : null}
    </>
  );

  const renderMessagesMenu =
    messageCount > 0 ? (
      <IconButton
        className="nav-button"
        width={4}
        size="large"
        aria-label="show new additional notifications"
        color="inherit"
        onClick={(e) => history.push(`/patient_portal/additional_messages/${props.messages[0].id}`)}
      >
        <Badge badgeContent={messageCount} color="error">
          <MailIcon />
        </Badge>
      </IconButton>
    ) : null;

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {!!props.notifications.length > 0 && (
        <MenuItem
          onClick={(e) => {
            history.push(`/patient_portal/notifications/${props.notifications[0].id}`);
          }}
        >
          {renderNotificationsMenu}
          <b className="app-bar-link">Notifications</b>
        </MenuItem>
      )}
      {messageCount > 0 && (
        <MenuItem>
          {renderMessagesMenu}
          <b className="app-bar-link">Messages</b>
        </MenuItem>
      )}
      <MenuItem>
        <a href="/patient_portal/settings" className="app-bar-link">
          <MdSettings />
          Settings
        </a>
      </MenuItem>
      <MenuItem>
        <a data-method="get" href="/users/sign_out" className="app-bar-link">
          <MdLogout />
          Logout
        </a>
      </MenuItem>
    </Menu>
  );
  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: patient.ui_color_hex,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            size="large"
            className="nav-button hamburger-btn"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            whiteSpace="no-wrap"
            component="div"
            sx={{ display: { sm: "block" }, fontSize: "18px" }}
            className={styles.welcomeContainer}
          >
            <b className={styles.greeting}>Your wellPORTAL </b> Patient Engagement Dashboard
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            {renderNotificationsMenu}
            {renderMessagesMenu}
            <IconButton
              className="nav-button hamburger-btn"
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Badge>
                <AccountCircle />
              </Badge>
            </IconButton>
          </Box>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              className="nav-button hamburger-btn"
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          className="drawer"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },

            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          <DrawerHeader className="drawer-header">
            <img src={logo} height="32px" />
            <IconButton onClick={handleDrawerToggle} size="large" sx={{ fontSize: "2em" }}>
              <MdChevronLeft />
            </IconButton>
          </DrawerHeader>

          {renderAppDrawer}
        </Drawer>
        <Drawer
          variant="permanent"
          className="drawer"
          sx={{
            borderRight: "none",
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
          open
        >
          <DrawerHeader className="drawer-header">
            <img src={logoUrl} height="24px" />
          </DrawerHeader>

          {renderAppDrawer}
        </Drawer>
      </Box>
      <Main
        className="patient-portal main"
        open={open}
        style={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Grid container direction="row" justifyContent="space-around" alignItems="center">
          <Grid item xs={12} pt={2} px={2} sx={{ pb: { xs: 8, sm: 4, m: 2 } }}>
            {props.children}
          </Grid>
        </Grid>
      </Main>
      {props.alerts && props.alerts.length > 0 && (
        <PatientAlert
          alerts={ props.alerts }
        />
      )}
    </Box>
  );
}
